import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DeleteConfirmModal from './DeleteConfirmModal';
import SuccessModal from './SuccessModal';

const API_BASE_URL = 'https://www.testometer.in:8084/api';

const TechnicalSkills = ({ selectedSkills, availableSkills, onSkillsChange, onAddNewSkill, onSkillsRefresh, onEditSkill }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [skillToDelete, setSkillToDelete] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const categories = ['all', ...new Set(availableSkills.map(skill => skill.category).filter(Boolean))];

  const filteredSkills = availableSkills.filter(skill => {
    const matchesSearch = skill.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || skill.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleSkillToggle = (skillId) => {
    const newSelectedSkills = selectedSkills.includes(skillId)
      ? selectedSkills.filter(id => id !== skillId)
      : [...selectedSkills, skillId];
    onSkillsChange(newSelectedSkills);
  };

  const selectedSkillNames = availableSkills
    .filter(skill => selectedSkills.includes(skill.id))
    .map(skill => skill.name);

  const handleAddNew = () => {
    if (onAddNewSkill) {
      onAddNewSkill();
    }
  };

  const handleEdit = (skill) => {
    if (onEditSkill) {
      onEditSkill(skill);
    }
  };

  const handleDelete = async (skillId) => {
    try {
      await axios.delete(`${API_BASE_URL}/master-data/technical-skills/${skillId}`);
      if (onSkillsRefresh) {
        onSkillsRefresh();
      }
      setShowSuccessModal(true);
      setSuccessMessage('Skill deleted successfully!');
    } catch (error) {
      console.error('Error deleting skill:', error);
    }
  };

  useEffect(() => {
    let timer;
    if (showSuccessModal) {
      timer = setTimeout(() => setShowSuccessModal(false), 3000);
    }
    return () => clearTimeout(timer);
  }, [showSuccessModal]);

  return (
    <>
      <div className="form-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 className="section-title blue">Technical Skills</h2>
          <button
            type="button"
            onClick={handleAddNew}
            className="btn btn-primary"
          >
            + Add New Skill
          </button>
        </div>
        
        <div className="form-group">
          <label className="form-label">
            Search and select your technical skills
          </label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field"
            placeholder="Search for skills..."
          />
        </div>

        <div className="form-group">
          <label className="form-label">Filter by Category</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="input-field"
          >
            {categories.map(category => (
              <option key={category} value={category}>
                {category === 'all' ? 'All Categories' : category}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">
            Available Skills ({filteredSkills.length} skills found)
          </label>
          <div className="skills-grid">
            {filteredSkills.map(skill => (
              <div
                key={skill.id}
                className={`skill-item ${selectedSkills.includes(skill.id) ? 'selected' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={selectedSkills.includes(skill.id)}
                  onChange={() => handleSkillToggle(skill.id)}
                  className="skill-checkbox"
                />
                <span className="skill-name" title={skill.name}>{skill.name}</span>
                <div className="skill-actions">
                  <button
                    type="button"
                    onClick={() => handleEdit(skill)}
                    className="btn btn-secondary"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSkillToDelete(skill.id); setShowDeleteModal(true); }}
                    className="btn btn-danger"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {selectedSkillNames.length > 0 && (
          <div className="form-group">
            <label className="form-label">Selected Skills ({selectedSkillNames.length})</label>
            <div className="selected-skills">
              {selectedSkillNames.map(skillName => (
                <span key={skillName} className="selected-skill-tag">
                  {skillName}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="form-help">
          Select the skills that best represent your technical expertise. You can search and filter by category to find specific skills.
        </div>
      </div>
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => { handleDelete(skillToDelete); setSkillToDelete(null); }}
        message="Are you sure you want to delete this skill? This action cannot be undone."
        title="Delete Skill"
      />
      <SuccessModal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)} message={successMessage} />
    </>
  );
};

export default TechnicalSkills; 